import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CheckCircle2, XCircle, Filter, ExternalLink } from "lucide-react";

const STATUS_PILL = {
  draft: "bg-slate-100 text-slate-700",
  pending_review: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-rose-100 text-rose-700",
  inactive: "bg-slate-100 text-slate-500",
};

const TABS = [
  { id: "pending_review", label: "Pending review" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
  { id: "draft", label: "Drafts" },
  { id: "all", label: "All" },
];

export default function AdminVendorServices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending_review");
  const [active, setActive] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");

  const refresh = () =>
    api
      .get("/admin/vendor-services")
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));

  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    if (tab === "all") return items;
    return items.filter((it) => it.status === tab);
  }, [items, tab]);

  const counts = useMemo(() => {
    const c = { all: items.length };
    items.forEach((it) => {
      c[it.status] = (c[it.status] || 0) + 1;
    });
    return c;
  }, [items]);

  const act = async (sid, decision) => {
    try {
      await api.patch(`/admin/vendor-services/${sid}`, {
        decision,
        admin_notes: adminNotes || null,
      });
      toast.success(decision === "approve" ? "Approved" : "Rejected");
      setActive(null);
      setAdminNotes("");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-vendor-services-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-violet-700">
          Admin · Vendor Services
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mt-1">Service catalog approvals</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Vendors design service packages on their side. Approve to make them visible to clinics in
          the catalog; reject with notes to coach the vendor on what to fix.
        </p>
      </div>

      <div
        className="flex flex-wrap items-center gap-2"
        data-testid="admin-vendor-services-tabs"
      >
        <Filter className="w-4 h-4 text-slate-400" />
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`admin-vendor-services-tab-${t.id}`}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${
              tab === t.id
                ? "bg-slate-900 text-white"
                : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
          >
            {t.label}
            <span className="text-[10px] font-bold opacity-70">{counts[t.id] || 0}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
          No services in this tab.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((row) => {
            const expanded = active?.id === row.id;
            return (
              <article
                key={row.id}
                className="rounded-2xl border border-slate-200 bg-white"
                data-testid={`admin-vendor-service-row-${row.id}`}
              >
                <div
                  className="px-5 py-4 flex flex-wrap items-start justify-between gap-3 cursor-pointer"
                  onClick={() => {
                    setActive(expanded ? null : row);
                    setAdminNotes(row.admin_notes || "");
                  }}
                >
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wide text-slate-400">
                      {row.vendor_name} {row.category && `· ${row.category}`}
                    </div>
                    <div className="font-semibold text-slate-900 mt-0.5">{row.name}</div>
                    {row.description && (
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2 max-w-xl">
                        {row.description}
                      </p>
                    )}
                    {row.custom_link && (
                      <span className="inline-flex items-center gap-1 mt-2 text-[10px] uppercase tracking-wide font-bold text-amber-800 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5">
                        <ExternalLink className="w-3 h-3" /> External link · exception
                      </span>
                    )}
                  </div>
                  <span
                    className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-1 rounded-full ${
                      STATUS_PILL[row.status] || STATUS_PILL.draft
                    }`}
                  >
                    {row.status?.replace("_", " ")}
                  </span>
                </div>
                {expanded && (
                  <div className="border-t border-slate-100 px-5 py-4 space-y-4 bg-slate-50/40">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                      <Stat label="Price" value={row.price != null ? `$${Number(row.price).toFixed(2)}` : "—"} />
                      <Stat label="Unit" value={row.price_unit || "—"} />
                      <Stat label="SLA" value={row.sla_days != null ? `${row.sla_days}d` : "—"} />
                      <Stat
                        label="Custom link"
                        value={
                          row.custom_link ? (
                            <a
                              href={row.custom_link}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-amber-700 hover:underline"
                            >
                              <ExternalLink className="w-3 h-3" /> Open
                            </a>
                          ) : (
                            "—"
                          )
                        }
                      />
                    </div>
                    {row.custom_link && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        <div className="font-bold uppercase tracking-wide text-[10px] text-amber-700 mb-1">
                          ⚠ External order link · review carefully
                        </div>
                        <div>
                          <span className="font-semibold">Vendor justification:</span>{" "}
                          {row.custom_link_reason || (
                            <em className="text-rose-700">No justification provided — reject and ask the vendor to explain.</em>
                          )}
                        </div>
                        <div className="text-[11px] text-amber-700 mt-1">
                          Nodera should only approve external links when the order genuinely cannot be fulfilled in-platform (e.g. compounded medications). Approving sends clinics off-platform.
                        </div>
                      </div>
                    )}
                    <label className="block">
                      <div className="text-xs font-semibold text-slate-700 mb-1">
                        Admin notes (visible to vendor on rejection)
                      </div>
                      <textarea
                        rows={3}
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        data-testid={`admin-vendor-service-notes-${row.id}`}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-violet-400"
                        placeholder="What should the vendor improve / confirm?"
                      />
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => act(row.id, "approve")}
                        data-testid={`admin-vendor-service-approve-${row.id}`}
                        className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg px-3 py-2"
                      >
                        <CheckCircle2 className="w-4 h-4" /> Approve
                      </button>
                      <button
                        onClick={() => act(row.id, "reject")}
                        data-testid={`admin-vendor-service-reject-${row.id}`}
                        className="inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold rounded-lg px-3 py-2"
                      >
                        <XCircle className="w-4 h-4" /> Reject
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-white border border-slate-100 p-2">
      <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
